import type { Site } from '../../services/sitesApi';

export const WORKSPACE_ALL_AUTHORIZED_SITES = '__all_authorized_sites__';

export type WorkspacePortfolioScope = {
  orgId: string;
  siteId: string;
  mode: 'site' | 'org';
  source: 'url' | 'stored' | 'none';
};

export function isAllAuthorizedSitesScope(value?: string | null): boolean {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === WORKSPACE_ALL_AUTHORIZED_SITES
    || normalized === 'all'
    || normalized === 'all_authorized_sites'
    || normalized === 'org';
}

export function resolveWorkspacePortfolioScope(input: {
  storedOrgId?: string | null;
  storedSiteId?: string | null;
  searchParams: URLSearchParams;
}): WorkspacePortfolioScope {
  const urlOrgId = String(input.searchParams.get('org_id') || input.searchParams.get('org') || '').trim();
  const urlSiteId = String(input.searchParams.get('site_id') || input.searchParams.get('site') || '').trim();
  const urlScope = String(input.searchParams.get('scope') || '').trim().toLowerCase();
  const storedOrgId = String(input.storedOrgId || '').trim();
  const storedSiteId = String(input.storedSiteId || '').trim();
  const orgId = urlOrgId || storedOrgId;
  const allAuthorized = isAllAuthorizedSitesScope(urlSiteId) || urlScope === 'org';
  const siteId = allAuthorized ? '' : (urlSiteId || storedSiteId);
  return {
    orgId,
    siteId,
    mode: allAuthorized ? 'org' : 'site',
    source: urlSiteId || urlOrgId || urlScope ? 'url' : orgId && siteId ? 'stored' : 'none',
  };
}

export type WorkspaceScopeOption = {
  value: string;
  label: string;
  secondary?: string;
};

export function buildWorkspaceSiteOptions(
  sites: Site[],
  activeSiteId: string,
  options?: { includeAllAuthorized?: boolean },
): WorkspaceScopeOption[] {
  const active = String(activeSiteId || '').trim();
  const seen = new Set<string>();
  const scopeOptions: WorkspaceScopeOption[] = [];
  if (options?.includeAllAuthorized) {
    scopeOptions.push({
      value: WORKSPACE_ALL_AUTHORIZED_SITES,
      label: 'All authorized sites',
      secondary: 'Org portfolio; counts require site coverage context',
    });
  }
  sites.forEach((site) => {
    const siteId = String(site.site_id || '').trim();
    if (!siteId || seen.has(siteId)) return;
    seen.add(siteId);
    scopeOptions.push({
      value: siteId,
      label: String(site.display_name || siteId).trim() || siteId,
      secondary: site.is_default ? `${siteId} · default site` : siteId,
    });
  });
  if (active && !seen.has(active)) {
    scopeOptions.splice(options?.includeAllAuthorized ? 1 : 0, 0, {
      value: active,
      label: active,
      secondary: 'Current site; metadata unavailable',
    });
  }
  return scopeOptions;
}
